import { checkWorkspaceSlug } from "./slugs";
import type { DnsRecord } from "./sendingDomains";
import { fillPlainPlaceholders, signatureName } from "./signature";
import { SENDER_LINE_MAX } from "./senderLine";
import { hasWorkingBlock, minutesOfTime, type DayKey, type WorkingHours } from "./workingHours";
import { WEEKDAY_NAMES, zonedInstant, zonedParts } from "./zones";
import { isOurAddress, parseAddress } from "./mailCustomer";
export { customerOf, isOurAddress, parseAddress, type CustomerCandidate, type CustomerOf, type CustomerVia, type MailAddress } from "./mailCustomer"; // the inbound customer rules (moved 2026-11-02)
export { isNoReplyAddress } from "./noReply"; // a robot sender (the no-reply and bounce block round)
/** The pure rules of an outbound reply's headers (M3 brief, "Outbound"). Under the subpath export `@omnabase/shared/mail`, which
 *  the main index does not pass on: nothing mail-related reaches the browser bundle. No library, no I/O. */

/** `Re: ` when the subject does not start so (any case or spacing), `[#<number>]` appended when this ticket's tag is absent. */
export function replySubject(subject: string, number: number): string {
  let s = subject.trim();
  if (!/^re:/i.test(s)) s = `Re: ${s}`;
  const tag = `[#${number}]`;
  const has = new RegExp(`\\[#${number}\\](?!\\d)`).test(s);
  return has ? s : `${s} ${tag}`;
}

/** The ids a reply names in `References`: trimmed, angle-bracketed, blanks dropped, each once at its first place, capped at the
 *  first id plus the last nine (mail programs keep threads on the first and the last few). */
export function referencesChain(ids: string[]): string[] {
  const out: string[] = [];
  for (const raw of ids) {
    const s = raw.trim(); if (!s) continue;
    const id = s.startsWith("<") ? s : `<${s.replace(/^<|>$/g, "")}>`;
    if (!out.includes(id)) out.push(id);
  }
  return out.length > 10 ? [out[0]!, ...out.slice(-9)] : out;
}

/** The From display name (2026-10-01, per project): `agent` = "<customer-facing name> from <project>", `person` = the customer-facing
 *  name alone, `team` = "<project> support"; the customer-facing name falls back to the team name, then the account's name; without any
 *  name, or without a person (the AI's own replies), the team form. The connector tags round (2026-10-27): `custom` = the project's own line
 *  (`senderLine`) filled with the signature's placeholders by the signature's own filling (`{logo}` dropped, unknown braces kept), runs of
 *  spaces made one, cut to 80 characters; without a person, or when the line comes out empty, the team form. */
export type SenderNameInput = { signAs: "agent" | "person" | "team" | "custom" | string; person: { displayName: string | null; customerName?: string | null; name: string | null } | null; projectName: string;
  /** the project's custom line (used only with `custom`) */ senderLine?: string; /** the workspace's name, for `{workspace}` */ workspaceName?: string; /** the person's customer-facing role, for `{role}` */ role?: string | null };
export function senderName(o: SenderNameInput): string {
  const project = o.projectName.trim();
  const who = o.signAs !== "team" && o.person ? signatureName(o.person) ?? "" : "";
  if (!who) return `${project} support`;
  if (o.signAs === "custom") return customSenderLine(o, who) || `${project} support`;
  return o.signAs === "person" ? who : `${who} from ${project}`;
}
/** the custom line filled in for one person ("" when nothing is left): one line, no logo, single spaces, at most 80 characters */
function customSenderLine(o: SenderNameInput, who: string): string {
  const line = (o.senderLine ?? "").replace(/[\r\n]+/g, " ");
  const filled = fillPlainPlaceholders(line, { name: who, project: o.projectName.trim(), workspace: (o.workspaceName ?? "").trim(), role: o.role ?? null }); // plain, never HTML (2026-10-28)
  return filled.replace(/[\u0000-\u001f\u007f]/g, "").replace(/\s+/g, " ").trim().slice(0, SENDER_LINE_MAX).trim();
}

/** The "From" value Amazon SES is handed beside a raw mail (2026-10-27): SES replaces the raw message's From header with it, so a bare
 *  address there drops the display name ("Christian from Omnalingo" arrived as support@omnalingo.com). A plain ASCII name is quoted, any
 *  other name is encoded the way mail headers encode words (RFC 2047, UTF-8, base64); no name gives the bare address. */
export function friendlyFrom(name: string | null | undefined, address: string): string {
  const n = (name ?? "").trim();
  if (!n) return address;
  if (/^[\x20-\x7e]*$/.test(n)) return `"${n.replace(/(["\\])/g, "\\$1")}" <${address}>`;
  const bytes = new TextEncoder().encode(n); let bin = ""; for (const b of bytes) bin += String.fromCharCode(b);
  return `=?UTF-8?B?${btoa(bin)}?= <${address}>`;
}

const OUR_DOMAIN = "omnabase.com";

/** The customer's copied people, minus what we never mail: our own domains, the project's support address (the forwarder), the
 *  customer (already in To) and a sender-unknown placeholder (`.invalid`). Lower-cased, deduplicated, order kept. Round four adds
 *  the workspace's suppressions. */
export function ccFor(o: { cc: string[]; customer: string; supportAddress: string | null | undefined; /** the workspace's addresses suppressed for replies (M3 round four); the caller reads them */ suppressed?: string[]; /** M7 round seven: the project's own verified sending address; never copied (the team's own mailbox would get our reply back) */ sendingAddress?: string | null }): string[] {
  const customer = o.customer.trim().toLowerCase(), support = o.supportAddress?.trim().toLowerCase() || null;
  const own = o.sendingAddress?.trim().toLowerCase() || null;
  const suppressed = new Set((o.suppressed ?? []).map((s) => s.trim().toLowerCase()));
  const out: string[] = [];
  for (const raw of o.cc) {
    const a = raw.trim().toLowerCase(); if (!a || !a.includes("@")) continue;
    if (isOurAddress(a) || a === customer || (support !== null && a === support) || (own !== null && a === own) || a.endsWith(".invalid") || suppressed.has(a)) continue;
    if (!out.includes(a)) out.push(a);
  }
  return out;
}

/* ---------- inbound (M3 round two): routing keys, loops, the customer of a forwarded mail, the setup catcher ---------- */

export const PLACEHOLDER_DOMAIN = "sender-unknown.invalid";
/** A workspace's mail domain (2026-10-01): every project address lives under it, `<project>@<workspace>.omnabase.com`; one domain for
 *  receiving and sending (the two old domains, `in.` and `reply.`, are history). */
export const workspaceMailDomain = (workspaceSlug: string): string => `${workspaceSlug}.${OUR_DOMAIN}`;
export const projectAddress = (workspaceSlug: string, mailLocal: string): string => `${mailLocal}@${workspaceMailDomain(workspaceSlug)}`;
/** the ticket's own address: a reply to it finds the ticket by the key even when the mail's headers are gone */
export const ticketReplyAddress = (workspaceSlug: string, mailLocal: string, key: string): string => `${mailLocal}+t.${key}@${workspaceMailDomain(workspaceSlug)}`;
/** a mail here closes the sender's address to our replies (List-Unsubscribe, mailto only) */
export const unsubscribeAddress = (workspaceSlug: string, mailLocal: string, key: string): string => `${mailLocal}+u.${key}@${workspaceMailDomain(workspaceSlug)}`;
/** M7 round seven: the address a project's replies leave from. A verified own domain → `<sendingLocal>@<domain>`; anything else (none,
 *  pending, failed, paused) → the project's address on our domain. Never a pending domain: SES would refuse it, and a customer would see
 *  a sender that fails DMARC. The Reply-To stays the ticket's own address on our domain whatever this answers. */
export function fromAddressFor(project: { mailLocal: string; sendingLocal: string }, domain: { domain: string; status: string } | null, workspaceSlug: string): string {
  return domain?.status === "verified" ? `${project.sendingLocal}@${domain.domain}` : projectAddress(workspaceSlug, project.mailLocal);
}
/** M7 round seven: the records a team copies to its DNS host: one CNAME per SES DKIM token in SES's order (required), then the DMARC
 *  suggestion (optional: a team with its own policy keeps it) */
export function dnsRecordsFor(domain: string, tokens: string[]): DnsRecord[] {
  return [
    ...tokens.map((t): DnsRecord => ({ type: "CNAME", name: `${t}._domainkey.${domain}`, value: `${t}.dkim.amazonses.com`, optional: false })),
    { type: "TXT", name: `_dmarc.${domain}`, value: "v=DMARC1; p=none;", optional: true },
  ];
}
export type Route = { address: string; mailLocal: string; /** the workspace's address, the label under omnabase.com (its current slug: an old one that redirects on the web does not receive) */ workspaceSlug: string; kind: "project" | "ticket" | "unsubscribe"; key: string | null };

/** The envelope recipients (SES's `receipt.recipients`) to routing keys: every address `<local>@<workspace>.omnabase.com` with one
 *  label under our domain that is a valid workspace address (never one of our own host names), lower-cased, each once, in envelope
 *  order (the first recipient's project wins inside a workspace). `+t.<key>` names a ticket, `+u.<key>` an unsubscribe, any other
 *  plus part (a forwarder's tag, an old token) the project. `notifications@…`, the bare domain, two labels deep, the old domains and
 *  anything not ours are dropped. */
export function routeRecipients(addresses: string[]): Route[] {
  const out: Route[] = [];
  for (const raw of addresses) {
    const address = raw.trim().toLowerCase();
    const at = address.lastIndexOf("@"); if (at <= 0) continue;
    const local = address.slice(0, at), domain = address.slice(at + 1);
    if (!domain.endsWith(`.${OUR_DOMAIN}`)) continue;
    const workspaceSlug = domain.slice(0, -(OUR_DOMAIN.length + 1));
    if (!workspaceSlug || workspaceSlug.includes(".") || checkWorkspaceSlug(workspaceSlug) !== null) continue;
    if (out.some((r) => r.address === address)) continue;
    const plus = local.indexOf("+");
    const mailLocal = plus < 0 ? local : local.slice(0, plus), tag = plus < 0 ? "" : local.slice(plus + 1);
    if (!mailLocal || mailLocal === "notifications") continue;
    const keyed = /^([tu])\.([a-z0-9]{26})$/.exec(tag);
    out.push({ address, mailLocal, workspaceSlug, kind: keyed ? (keyed[1] === "t" ? "ticket" : "unsubscribe") : "project", key: keyed ? keyed[2]! : null });
  }
  return out;
}

export type LoopReason = "auto-submitted" | "precedence" | "list" | "empty-sender" | "report" | "daemon" | "ours";
/** What is dropped as a loop (step 3): automatic mail, bounce reports, our own mail, somebody else's mailing list. The setup test
 *  mail is caught before this runs. `headers` are lower-cased names (the kept list plus `content-type`). */
export function loopReason(headers: Record<string, string | undefined>, envelope: { source: string | null | undefined; supportAddress: string | null | undefined; /** M7 round seven: the project's own verified sending address: a mail from it is ours */ sendingAddress?: string | null }): LoopReason | null {
  const source = (envelope.source ?? "").trim();
  if (source === "" || source === "<>") return "empty-sender";
  const ct = (headers["content-type"] ?? "").split(";")[0]!.trim().toLowerCase();
  if (ct === "multipart/report") return "report";
  const from = parseAddress(headers.from)?.email ?? "";
  if (/^(mailer-daemon|postmaster)@/.test(from)) return "daemon";
  if (from && (isOurAddress(from) || from === envelope.sendingAddress?.trim().toLowerCase())) return "ours";
  const auto = (headers["auto-submitted"] ?? "").trim().toLowerCase();
  if (auto && auto !== "no") return "auto-submitted";
  const precedence = (headers.precedence ?? "").trim().toLowerCase();
  if (precedence === "bulk" || precedence === "junk" || precedence === "auto_reply") return "precedence";
  if (precedence === "list") {
    const support = envelope.supportAddress?.trim().toLowerCase();
    if (!support) return "list";
    const post = (headers["list-post"] ?? "").toLowerCase(), id = (headers["list-id"] ?? "").toLowerCase();
    if (post.includes(`mailto:${support}`) || id.includes(support.replace("@", "."))) return null;
    return "list";
  }
  return null;
}

/** Google's forwarding confirmation: today a link (`/mail/vf-…` on Google's own host; the cancel link `uf-` never counts), in the
 *  older flow a code. */
export function forwardingConfirmation(text: string): { link: string | null; code: string | null } {
  const link = /https:\/\/mail-settings\.google\.com\/mail\/vf-[^\s<>"'\])]+/.exec(text ?? "")?.[0] ?? null;
  const code = /confirmation code:\s*(\d{6,12})/i.exec(text ?? "")?.[1] ?? null;
  return { link, code };
}

/** The per-project placeholder customer of a mail whose sender could not be found: never deliverable, never routable. */
export const senderUnknownAddress = (mailLocal: string) => `unknown+${mailLocal}@${PLACEHOLDER_DOMAIN}`;
export const isSenderUnknownAddress = (email: string) => email.toLowerCase().endsWith(`@${PLACEHOLDER_DOMAIN}`);

/* ---------- quoted text (M3 round three): our own markers; the text parser runs in packages/server ---------- */

/** the attributes of one tag, names lower-cased, quotes stripped */
function attrsOf(tag: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const m of tag.matchAll(/([\w:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g)) out[m[1]!.toLowerCase()] = (m[2] ?? m[3] ?? m[4] ?? "").trim();
  return out;
}
const classTokens = (a: Record<string, string>) => (a.class ?? "").toLowerCase().split(/\s+/);
/** does this opening tag start a quoted block? Gmail's div.gmail_quote, blockquote type=cite (Apple Mail, Thunderbird), Outlook's
 *  #divRplyFwdMsg and #appendonsend, Outlook's desktop border-top rule; Thunderbird's attribution line goes with its quote */
function isQuoteMarker(name: string, a: Record<string, string>): boolean {
  if (name === "blockquote" && (a.type ?? "").toLowerCase() === "cite") return true;
  if (name !== "div") return false;
  const cls = classTokens(a), id = (a.id ?? "").toLowerCase();
  if (cls.includes("gmail_quote") || cls.includes("moz-cite-prefix")) return true;
  if (id === "divrplyfwdmsg" || id === "appendonsend") return true;
  return /border:\s*none;\s*border-top:\s*solid\s+#?[0-9a-z]{3,8}\s+1\.0pt/i.test(a.style ?? "");
}
/** is there anything of the customer's own before the cut? text, or a picture; whitespace, breaks and empty elements are not */
const hasContent = (html: string) => /<img\b/i.test(html) || html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").trim().length > 0;

/** The fresh part of a reply's HTML, cut at the first quote marker in document order, on the raw HTML before `cleanHtml` (which
 *  strips class, id and type). Markers are tags, never text. With no marker, or nothing fresh before it, the HTML comes back
 *  unchanged: a mail with no fresh part shows the quoted part. */
export function freshHtml(html: string): { html: string; quoted: boolean } {
  for (const m of html.matchAll(/<([a-zA-Z][\w:-]*)\b([^>]*)>/g)) {
    if (!isQuoteMarker(m[1]!.toLowerCase(), attrsOf(m[2] ?? ""))) continue;
    const fresh = html.slice(0, m.index);
    return hasContent(fresh) ? { html: fresh, quoted: true } : { html, quoted: false };
  }
  return { html, quoted: false };
}

/** Where Outlook's "From: … Sent: …" header block starts in plain text (the parser may miss it): the offset of the `____` line above
 *  it when there is one, else of the `From:` line; null when no `From:` line is followed by a `Sent:` line. German Outlook's
 *  `Von:` / `Gesendet:` too. */
export function outlookQuoteStart(text: string): number | null {
  const lines = text.split("\n"); let offset = 0;
  const starts: number[] = [];
  for (const l of lines) { starts.push(offset); offset += l.length + 1; }
  for (let i = 0; i + 1 < lines.length; i++) {
    const a = lines[i]!.replace(/\r$/, ""), b = lines[i + 1]!.replace(/\r$/, "");
    if (!/^(From|Von):\s*\S/.test(a) || !/^(Sent|Gesendet):\s*\S/.test(b)) continue;
    const prev = i > 0 ? lines[i - 1]!.replace(/\r$/, "") : "";
    return /^_{5,}\s*$/.test(prev) ? starts[i - 1]! : starts[i]!;
  }
  return null;
}

/* ---------- notifications (M3 round seven): the due rule, pure ---------- */

/** the window a notification row waits for more events and for the person to open the ticket; also the least gap between two
 *  notification mails to one person */
export const NOTIFY_DELAY_MS = 120_000;
export type { WorkingHours } from "./workingHours";
/** some block of the local weekday holds the local minute (from <= minute < to); the hours are the person's own, else the workspace's default,
 *  read in the person's zone (the working hours round, 2026-11-01; every block of the day since the hours editor round, 2026-11-02) */
export function insideWorkingHours(at: string, hours: WorkingHours, zone: string): boolean {
  const p = zonedParts(new Date(at), zone);
  const minutes = p.hh * 60 + p.mm;
  return hours.days[p.weekday as DayKey].some((b) => minutes >= minutesOfTime(b.from) && minutes < minutesOfTime(b.to));
}
/** the first block start strictly after `at` (looking eight days ahead; the same day's later blocks first, so a lunch break ends at the
 *  afternoon's start), as an ISO instant; the local hour stays the same across a daylight-saving change; a week without any block: not moved
 *  (a mail late is better than a mail never) */
export function nextWorkingMorning(at: string, hours: WorkingHours, zone: string): string {
  const start = new Date(at);
  const p = zonedParts(start, zone);
  for (let i = 0; i <= 8; i++) {
    const day = new Date(Date.UTC(p.y, p.m - 1, p.d + i, 12, 0)); // noon UTC of that calendar day: safe from any offset
    for (const block of hours.days[WEEKDAY_NAMES[day.getUTCDay()] as DayKey]) {
      const from = minutesOfTime(block.from);
      const begins = zonedInstant(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate(), Math.floor(from / 60), from % 60, zone);
      if (begins.getTime() > start.getTime()) return begins.toISOString();
    }
  }
  return start.toISOString();
}
/** when a notification row is due: its first event plus the delay, held until the person is back from "away until", then, with the
 *  person's quiet switch on, until their next working block when that moment is outside their hours (the working hours round: `hours` is the
 *  person's own, else the default; `zone` the person's; a week with no block never holds) */
export type HoldOptions = { quiet: boolean; hours: WorkingHours; zone: string; awayUntil?: string | null };
export function notifyDueAt(firstAt: string, o: HoldOptions): string {
  return heldUntil(new Date(new Date(firstAt).getTime() + NOTIFY_DELAY_MS), o);
}
/** when a browser notification about an event may go (browser notifications round one, 2026-10-18): the mail's rule without its two
 *  minutes, so a pop-up goes the moment something happens; "away until" and quiet hours hold it as they hold the mail */
export function pushDueAt(at: string, o: HoldOptions): string {
  return heldUntil(new Date(at), o);
}
/** a moment held until the person is back from "away until", then, with the quiet switch on and some block in the week, until the next
 *  block when that moment falls outside the hours */
function heldUntil(at: Date, o: HoldOptions): string {
  let due = at;
  if (o.awayUntil && new Date(o.awayUntil).getTime() > due.getTime()) due = new Date(o.awayUntil);
  if (o.quiet && hasWorkingBlock(o.hours) && !insideWorkingHours(due.toISOString(), o.hours, o.zone)) return nextWorkingMorning(due.toISOString(), o.hours, o.zone);
  return due.toISOString();
}

/** "Show pictures" (2026-10-02): the display HTML with its blocked pictures' addresses put back, each only when it is an `https:`
 *  address (the server set them aside in `data-blocked-src` after cleaning; the page checks again before the browser loads one) */
export function revealPictures(html: string): string {
  return html.replace(/ data-blocked-src="(https:\/\/[^"\s]+)"/g, (_all, src: string) => ` src="${src}"`);
}
